/**
 * Reporte de errores de cliente (React/browser) a Sentry — HTTP directo al DSN público.
 *
 * Consistente con el reporter server-side (sentry-report.ts): sin SDK pesado.
 * Usa el DSN público (`VITE_SENTRY_DSN`), que es seguro de exponer en el bundle
 * (está diseñado para ingestar desde el navegador). Captura:
 *   - errores no manejados (window.onerror / unhandledrejection)
 *   - errores de render React (vía AppErrorBoundary)
 * Con environment + release + tenant tags.
 */

interface DsnParts {
  host: string;
  projectId: string;
  publicKey: string;
}

function parseDsn(dsn: string): DsnParts | null {
  try {
    const u = new URL(dsn.trim());
    const publicKey = u.username;
    const projectId = u.pathname.replace(/^\//, "").replace(/\/$/, "");
    if (!publicKey || !projectId || !u.host) return null;
    return { host: u.host, projectId, publicKey };
  } catch {
    return null;
  }
}

const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? "";
const ENVIRONMENT = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? (import.meta.env.PROD ? "production" : "development");
const RELEASE = (import.meta.env.VITE_APP_RELEASE as string | undefined) ?? "food-market@dev";

let parts: DsnParts | null = null;
let initialized = false;

function eventId(): string {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/-/g, "");
}

/** Tags contextuales (ej. tenant) que se adjuntan a cada evento. */
const contextTags: Record<string, string> = {};
export function setSentryClientTag(key: string, value: string | null | undefined): void {
  if (value) contextTags[key] = value;
  else delete contextTags[key];
}

function send(payload: Record<string, unknown>): void {
  if (!parts) return;
  const url = `https://${parts.host}/api/${parts.projectId}/store/`;
  const auth = `Sentry sentry_version=7, sentry_client=fm-web/1.0, sentry_key=${parts.publicKey}`;
  // keepalive permite que el envío sobreviva a la navegación.
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Sentry-Auth": auth },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined);
}

export function captureClientError(
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  if (!parts) return;
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack ?? null : null;
  send({
    event_id: eventId(),
    timestamp: new Date().toISOString(),
    platform: "javascript",
    level: "error",
    environment: ENVIRONMENT,
    release: RELEASE,
    tags: { ...contextTags },
    extra: { ...extra, url: typeof location !== "undefined" ? location.href : undefined },
    exception: {
      values: [{ type: error instanceof Error ? error.name : "Error", value: message, stacktrace: stack ? { frames: [{ filename: stack.slice(0, 1000) }] } : undefined }],
    },
  });
}

/** Inicializa los listeners globales. Idempotente. No-op si no hay DSN. */
export function initSentryClient(): void {
  if (initialized || typeof window === "undefined") return;
  parts = parseDsn(DSN);
  if (!parts) return; // sin DSN configurado → no-op silencioso
  initialized = true;

  window.addEventListener("error", (e) => {
    captureClientError(e.error ?? new Error(e.message), { source: "window.onerror" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    captureClientError(e.reason ?? new Error("unhandledrejection"), { source: "unhandledrejection" });
  });
}
